import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Image,
  Alert,
} from 'react-native';
import { InsetModal as Modal } from '../../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { colors, spacing, typography } from '../../../theme';
import { Button } from '../../design-system/Button';
import { Input } from '../../design-system/Input';
import { LocationPicker } from '../../LocationPicker';
import { HeaderAsset } from '../types/messageForm.types';
import { apiService } from '../../../services/api';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

interface TemplateHeaderAssetInputProps {
  headerAsset?: HeaderAsset;
  onAssetChange: (asset: HeaderAsset) => void;
  headerType?: 'image' | 'video' | 'document' | 'location'; // Type from template
  isParameterized?: boolean;
  campaignName?: string;
  businessId?: string;
}

const toProxyMediaUrl = (mediaUrl: string, mimeType: string): string => {
  if (mediaUrl.includes('/api/storage/media/proxy')) return mediaUrl;
  const apiBaseUrl = process.env.EXPO_PUBLIC_API_URL || process.env.API_BASE_URL || 'https://api.connect.dislix.com';
  const base = apiBaseUrl.replace(/\/$/, '');
  const encodedUrl = encodeURIComponent(mediaUrl);
  return `${base}/api/storage/media/proxy?url=${encodedUrl}&mimeType=${encodeURIComponent(mimeType)}`;
};

export const TemplateHeaderAssetInput: React.FC<TemplateHeaderAssetInputProps> = ({
  headerAsset,
  onAssetChange,
  headerType,
  isParameterized = false,
  campaignName = 'default',
  businessId,
}) => {
  const styles = useThemedStyles(getStyles);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleSelectImage = async () => {
    try {
      console.log('[DEBUG] Opening image picker...');
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [16, 9],
        quality: 0.8,
      });

      console.log('[DEBUG] Image picker result:', JSON.stringify(result, null, 2));

      if (!result.canceled && result.assets?.[0]) {
        setIsUploading(true);
        const asset = result.assets[0];
        const fileName = asset.uri.split('/').pop() || `image_${Date.now()}.jpg`;
        console.log('[DEBUG] Uploading image:', fileName, 'URI:', asset.uri);

        try {
          const response = await apiService.uploadMedia(asset.uri, fileName, 'image/jpeg', 'default-whatsapp');
          console.log('[DEBUG] Upload response:', JSON.stringify(response, null, 2));

          if (response.statusCode === 1000 && response.data) {
            // Use mediaId if available, otherwise use single proxy URL (never double-proxy)
            let headerUrl = response.data.mediaUrl;
            // If mediaUrl is already a proxy URL, do not wrap again
            const isProxyUrl = headerUrl.includes('/api/storage/media/proxy');
            if (!isProxyUrl) {
              const apiBaseUrl = process.env.EXPO_PUBLIC_API_URL || process.env.API_BASE_URL || 'https://api.connect.dislix.com';
              const base = apiBaseUrl.replace(/\/$/, '');
              const encodedUrl = encodeURIComponent(headerUrl);
              headerUrl = `${base}/api/storage/media/proxy?url=${encodedUrl}&mimeType=${encodeURIComponent('image/jpeg')}`;
            }
            onAssetChange({
              type: 'image',
              mediaId: response.data.mediaId,
              url: headerUrl,
              isParameterized,
            });
            Alert.alert('Success', 'Image uploaded successfully');
          } else {
            throw new Error(response.errors?.[0]?.message || 'Upload failed');
          }
        } catch (uploadError: any) {
          Alert.alert('Upload Error', uploadError.message || 'Failed to upload image');
        } finally {
          setIsUploading(false);
        }
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to select image');
      console.error('Error selecting image:', error);
    }
  };

  const handleSelectVideo = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        quality: 0.8,
      });

      if (!result.canceled && result.assets?.[0]) {
        setIsUploading(true);
        const asset = result.assets[0];
        const fileName = asset.uri.split('/').pop() || `video_${Date.now()}.mp4`;

        try {
          const response = await apiService.uploadMedia(asset.uri, fileName, 'video/mp4', 'default-whatsapp');

          if (response.statusCode === 1000 && response.data) {
            onAssetChange({
              type: 'video',
              mediaId: response.data.mediaId,
              url: toProxyMediaUrl(response.data.mediaUrl, 'video/mp4'),
              isParameterized,
            });
            Alert.alert('Success', 'Video uploaded successfully');
          } else {
            throw new Error(response.errors?.[0]?.message || 'Upload failed');
          }
        } catch (uploadError: any) {
          Alert.alert('Upload Error', uploadError.message || 'Failed to upload video');
        } finally {
          setIsUploading(false);
        }
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to select video');
      console.error('Error selecting video:', error);
    }
  };

  const handleSelectDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/msword', 'application/vnd.ms-excel'],
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        setIsUploading(true);
        const doc = result.assets[0];
        const fileName = doc.name || `document_${Date.now()}.pdf`;
        const mimeType = doc.mimeType || 'application/pdf';

        try {
          const response = await apiService.uploadMedia(doc.uri, fileName, mimeType, 'default-whatsapp');

          if (response.statusCode === 1000 && response.data) {
            onAssetChange({
              type: 'document',
              mediaId: response.data.mediaId,
              url: toProxyMediaUrl(response.data.mediaUrl, mimeType),
              filename: response.data.mediaName || fileName,
              isParameterized,
            });
            Alert.alert('Success', 'Document uploaded successfully');
          } else {
            throw new Error(response.errors?.[0]?.message || 'Upload failed');
          }
        } catch (uploadError: any) {
          Alert.alert('Upload Error', uploadError.message || 'Failed to upload document');
        } finally {
          setIsUploading(false);
        }
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to select document');
      console.error('Error selecting document:', error);
    }
  };

  const handleFilenameChange = (newFilename: string) => {
    if (!headerAsset) return;
    onAssetChange({ ...headerAsset, filename: newFilename });
  };

  const handleLocationSelect = (location: { latitude: number; longitude: number; address?: string }) => {
    onAssetChange({
      type: 'location',
      mediaId: JSON.stringify(location),
      isParameterized,
    });
    setShowLocationPicker(false);
  };

  // A template's default headerAsset (e.g. { type: 'document', isParameterized: true })
  // has no url/mediaId — it's just a placeholder saying "this template needs a file",
  // not an actual attached file. Only a real url/mediaId means something was
  // genuinely uploaded/selected and is safe to preview or submit.
  const hasRealAsset = !!(headerAsset && (headerAsset.url || headerAsset.mediaId));

  const renderAssetPreview = () => {
    if (!headerAsset) return null;

    if (!hasRealAsset && headerAsset.type !== 'location') {
      return (
        <View style={styles.previewContainer}>
          <View style={styles.emptyAssetPlaceholder}>
            <Ionicons name="cloud-upload-outline" size={40} color={colors.text.secondary} />
            <Text style={styles.emptyAssetText}>No file selected yet — tap below to upload</Text>
          </View>
        </View>
      );
    }

    switch (headerAsset.type) {
      case 'image':
        return (
          <View style={styles.previewContainer}>
            {headerAsset.url ? (
              <Image
                source={{ uri: headerAsset.url }}
                style={styles.imagePreview}
                onError={e => {
                  console.error('[Image Preview Error]', e.nativeEvent);
                  Alert.alert('Image Error', 'Failed to load image preview.');
                }}
                onLoad={() => {
                  console.log('[Image Preview Loaded]', headerAsset.url);
                }}
              />
            ) : (
              <View style={[styles.imagePreview, { backgroundColor: colors.lightGray }]} />
            )}
          </View>
        );
      case 'video':
        return (
          <View style={styles.previewContainer}>
            <View style={styles.videoPlaceholder}>
              <Ionicons name="play-circle" size={48} color={colors.primary} />
              <Text style={styles.videoPlaceholderText}>Video Selected</Text>
            </View>
          </View>
        );
      case 'document':
        return (
          <View style={styles.previewContainer}>
            <View style={styles.documentPreview}>
              <Ionicons name="document" size={48} color={colors.primary} />
              <Text style={styles.documentPreviewText} numberOfLines={1}>
                {headerAsset.filename || 'Document'}
              </Text>
            </View>
            <Input
              label="File name"
              value={headerAsset.filename || ''}
              onChangeText={handleFilenameChange}
              placeholder="document.pdf"
              containerStyle={styles.filenameInput}
              latin
            />
          </View>
        );
      case 'location':
        return (
          <View style={styles.previewContainer}>
            <View style={styles.locationPreview}>
              <Ionicons name="location" size={48} color={colors.primary} />
              <Text style={styles.locationPreviewText}>Location Selected</Text>
            </View>
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.label}>
            Header Asset
            {isParameterized && <Text style={styles.paramLabel}> (Dynamic)</Text>}
          </Text>
          {headerType && (
            <Text style={styles.hint}>
              {headerType === 'image' && 'JPG, PNG (Recommended 16:9)'}
              {headerType === 'video' && 'MP4 (Max 5MB)'}
              {headerType === 'document' && 'PDF, DOC, XLS'}
              {headerType === 'location' && 'Coordinates & Address'}
            </Text>
          )}
        </View>

        {renderAssetPreview()}

        {headerType && (
          <>
            {headerType === 'image' && (
              <Button
                title={isUploading ? 'Uploading...' : (headerAsset ? 'Change Image' : 'Upload Image')}
                onPress={handleSelectImage}
                variant={headerAsset ? 'secondary' : 'primary'}
                size="sm"
                style={styles.selectButton}
                disabled={isUploading}
                leftIcon={isUploading ? <ActivityIndicator size="small" color={colors.background.primary} /> : <Ionicons name="image" size={16} color={headerAsset ? colors.text.secondary : colors.background.primary} />}
              />
            )}
            {headerType === 'video' && (
              <Button
                title={isUploading ? 'Uploading...' : (headerAsset ? 'Change Video' : 'Upload Video')}
                onPress={handleSelectVideo}
                variant={headerAsset ? 'secondary' : 'primary'}
                size="sm"
                style={styles.selectButton}
                disabled={isUploading}
                leftIcon={isUploading ? <ActivityIndicator size="small" color={colors.background.primary} /> : <Ionicons name="videocam" size={16} color={headerAsset ? colors.text.secondary : colors.background.primary} />}
              />
            )}
            {headerType === 'document' && (
              <Button
                title={isUploading ? 'Uploading...' : (headerAsset ? 'Change Document' : 'Upload Document')}
                onPress={handleSelectDocument}
                variant={headerAsset ? 'secondary' : 'primary'}
                size="sm"
                style={styles.selectButton}
                disabled={isUploading}
                leftIcon={isUploading ? <ActivityIndicator size="small" color={colors.background.primary} /> : <Ionicons name="document" size={16} color={headerAsset ? colors.text.secondary : colors.background.primary} />}
              />
            )}
            {headerType === 'location' && (
              <Button
                title={headerAsset ? 'Change Location' : 'Select Location'}
                onPress={() => setShowLocationPicker(true)}
                variant={headerAsset ? 'secondary' : 'primary'}
                size="sm"
                style={styles.selectButton}
                leftIcon={<Ionicons name="location" size={16} color={headerAsset ? colors.text.secondary : colors.background.primary} />}
              />
            )}
          </>
        )}

        {headerAsset && (
          <Button
            title="Remove Asset"
            onPress={() => onAssetChange({})}
            variant="ghost"
            size="sm"
            style={styles.removeButton}
            leftIcon={<Ionicons name="trash" size={16} color={colors.error} />}
          />
        )}
      </View>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <Modal
          visible={showLocationPicker}
          animationType="slide"
          onRequestClose={() => setShowLocationPicker(false)}
        >
          <LocationPicker
            visible={showLocationPicker}
            onLocationSelect={handleLocationSelect}
            onClose={() => setShowLocationPicker(false)}
          />
        </Modal>
      )}
    </>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  header: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  paramLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.primary,
  },
  hint: {
    fontSize: 13,
    color: colors.text.secondary,
  },
  previewContainer: {
    marginVertical: spacing.md,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.background.primary,
  },
  imagePreview: {
    width: '100%',
    height: 180,
    borderRadius: 8,
  },
  videoPlaceholder: {
    width: '100%',
    height: 180,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPlaceholderText: {
    marginTop: spacing.sm,
    fontSize: 13,
    color: colors.text.secondary,
  },
  documentPreview: {
    width: '100%',
    height: 120,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  documentPreviewText: {
    marginTop: spacing.sm,
    fontSize: 13,
    color: colors.text.secondary,
    maxWidth: '80%',
  },
  filenameInput: {
    marginTop: spacing.sm,
    marginBottom: 0,
  },
  locationPreview: {
    width: '100%',
    height: 120,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  locationPreviewText: {
    marginTop: spacing.sm,
    fontSize: 13,
    color: colors.text.secondary,
  },
  emptyAssetPlaceholder: {
    width: '100%',
    height: 120,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.border,
  },
  emptyAssetText: {
    marginTop: spacing.sm,
    fontSize: 13,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: spacing.md,
  },
  selectButton: {
    marginTop: spacing.md,
  },
  removeButton: {
    marginTop: spacing.sm,
  },
});
